import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/apis/api.service';
import { Observable } from 'rxjs';
import { DriverMetric } from '../components/driver-metrics-row/driver-metrics-row.component';

export type DriverHomeStatus = 'AVAILABLE' | 'ON_BREAK' | 'IN_SERVICE';

export interface DriverHomeState {
    status: DriverHomeStatus;
    is_available: boolean;
    shift_id: number | null;
    /** Escala prevista de hoje (informativo). */
    on_planned_schedule?: boolean;
    vehicle_id: number | null;
    /** true quando a viatura veio da alocação operacional. */
    vehicle_from_allocation?: boolean;
    shift_start_at: string | null;
    services_completed_today: number;
    fuel_level: number | null;
    known_location_name: string | null;
    active_service_id: number | null;
    vehicle_code: string | null;
    vehicle_name: string | null;
}

export interface DriverAvailableVehicle {
    vehicle_id: number;
    name: string | null;
    license_plate: string | null;
    is_current: boolean;
}

export interface DriverAvailableVehiclesResponse {
    current_vehicle_id: number | null;
    allocation_id: number | null;
    vehicles: DriverAvailableVehicle[];
}

export interface DriverHomeView {
    status: DriverHomeStatus;
    statusRing: {
        title: string;
        subtitle: string;
        icon: string;
        variant: 'success' | 'danger' | 'coral' | 'muted';
    };
    metrics: DriverMetric[];
    card: {
        label: string;
        text: string;
    };
    pauseButton: {
        label: string;
        icon: string;
        disabled: boolean;
    };
}

@Injectable({ providedIn: 'root' })
export class DriverHomeService {
    constructor(private readonly api: ApiService) {}

    getHome(): Observable<DriverHomeState> {
        return this.api.getSub<DriverHomeState>('driver/home');
    }

    setAvailability(is_available: boolean): Observable<DriverHomeState> {
        return this.api.patchSub<DriverHomeState>('driver/availability', { is_available });
    }

    getAvailableVehicles(): Observable<DriverAvailableVehiclesResponse> {
        return this.api.getSub<DriverAvailableVehiclesResponse>('driver/vehicles');
    }

    mapToView(home: DriverHomeState): DriverHomeView {
        return {
            status: home.status,
            statusRing: this.mapStatusRing(home),
            metrics: this.mapMetrics(home),
            card: this.mapCard(home),
            pauseButton: this.mapPauseButton(home),
        };
    }

    private mapStatusRing(home: DriverHomeState): DriverHomeView['statusRing'] {
        switch (home.status) {
            case 'IN_SERVICE':
                return {
                    title: 'Em atendimento',
                    subtitle: 'atendimento em andamento',
                    icon: 'heroicons_outline:truck',
                    variant: 'coral',
                };
            case 'ON_BREAK':
                return {
                    title: 'Em pausa',
                    subtitle: 'pausa operacional',
                    icon: 'heroicons_outline:pause-circle',
                    variant: 'danger',
                };
            case 'AVAILABLE':
            default: {
                const location = home.known_location_name?.trim() || 'na garagem';
                const vehicleHint = home.vehicle_code?.trim()
                    ? ` · ${home.vehicle_code.trim()}`
                    : home.vehicle_name?.trim()
                      ? ` · ${home.vehicle_name.trim()}`
                      : '';
                const scheduleHint = home.on_planned_schedule === false ? ' · fora da escala prevista' : '';
                return {
                    title: 'Disponível',
                    subtitle: `${location}${vehicleHint}${scheduleHint} · aguardando despacho`,
                    icon: 'heroicons_outline:check-circle',
                    variant: 'success',
                };
            }
        }
    }

    private mapMetrics(home: DriverHomeState): DriverMetric[] {
        let entrada: DriverMetric = { value: '—', label: 'entrada' };
        if (home.shift_start_at) {
            const date = new Date(home.shift_start_at);
            entrada = {
                value: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                subValue: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
                label: 'entrada prevista',
            };
        }

        const fuelValue =
            home.fuel_level !== null && home.fuel_level !== undefined ? `${Math.round(home.fuel_level)}%` : '—';
        const fuelVariant =
            home.fuel_level !== null && home.fuel_level !== undefined && home.fuel_level < 30
                ? ('warning' as const)
                : ('success' as const);

        return [
            { value: String(home.services_completed_today), label: 'hoje' },
            entrada,
            { value: fuelValue, label: 'combustível', variant: fuelVariant },
        ];
    }

    private mapCard(home: DriverHomeState): DriverHomeView['card'] {
        switch (home.status) {
            case 'IN_SERVICE': {
                const vehicleLabel = this.formatVehicleLabel(home);
                const base = home.active_service_id
                    ? `Atendimento #${home.active_service_id} em curso.`
                    : 'Atendimento em curso.';
                return {
                    label: 'atendimento ativo',
                    text: vehicleLabel
                        ? `${base} Viatura ${vehicleLabel}. Siga as instruções da central.`
                        : `${base} Siga as instruções da central.`,
                };
            }
            case 'ON_BREAK':
                return {
                    label: 'em pausa',
                    text: 'Você está em pausa operacional. Toque em "Voltar ao plantão" quando estiver pronto para receber despachos.',
                };
            case 'AVAILABLE':
            default: {
                const vehicleLabel = this.formatVehicleLabel(home);
                const scheduleNote =
                    home.on_planned_schedule === false
                        ? ' Você está fora da escala prevista de hoje; a central pode alocar mesmo assim.'
                        : '';
                if (vehicleLabel) {
                    return {
                        label: home.vehicle_from_allocation ? 'viatura do atendimento' : 'viatura alocada',
                        text: `Viatura ${vehicleLabel}. A central irá direcionar o próximo atendimento.${scheduleNote}`,
                    };
                }
                return {
                    label: 'online',
                    text: `Você está online e pode receber despachos.${scheduleNote} Aguarde a central alocar viatura/atendimento.`,
                };
            }
        }
    }

    private formatVehicleLabel(home: DriverHomeState): string | null {
        const code = home.vehicle_code?.trim();
        const name = home.vehicle_name?.trim();
        if (code && name && code !== name) {
            return `${code} (${name})`;
        }
        return code || name || null;
    }

    private mapPauseButton(home: DriverHomeState): DriverHomeView['pauseButton'] {
        const inService = home.status === 'IN_SERVICE' || !!home.active_service_id;
        const disabled = inService;

        if (inService) {
            return {
                label: 'Marcar pausa',
                icon: 'heroicons_outline:pause-circle',
                disabled: true,
            };
        }

        if (home.status === 'ON_BREAK') {
            return {
                label: 'Voltar ao plantão',
                icon: 'heroicons_outline:play-circle',
                disabled,
            };
        }

        return {
            label: 'Marcar pausa',
            icon: 'heroicons_outline:pause-circle',
            disabled,
        };
    }
}
